import { useEffect, useRef, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
    addMonths,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    isValid,
    parse,
    startOfMonth,
    startOfWeek
} from 'date-fns';

const ISO = 'yyyy-MM-dd';
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = Array.from({ length: 15 }, (_, i) => 2018 + i);

const buildGrid = (month) => {
    const days = [];
    let day = startOfWeek(startOfMonth(month));
    const last = endOfWeek(endOfMonth(month));
    while (day <= last) {
        days.push(day);
        day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
    }
    return days;
};

// Date picker matching the Eadin design system — calendar popover with
// month/year dropdowns and Clear / Today footer actions.
export const DatePickerField = ({
    value,
    onChange,
    allowClear = true,
    className = '',
    testId
}) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const parsed = value ? parse(value, ISO, new Date()) : undefined;
    const valid = parsed && isValid(parsed) ? parsed : undefined;
    const [month, setMonth] = useState(valid || new Date());

    useEffect(() => {
        if (open) setMonth(valid || new Date());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const pick = (date) => {
        onChange(format(date, ISO));
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <button
                type="button"
                data-testid={testId}
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center gap-2 w-full h-[38px] px-3 rounded-md border border-gray-300 bg-[#E8F0FE] text-sm text-left ${valid ? 'text-gray-900' : 'text-gray-400'}`}
            >
                <CalendarIcon className="h-4 w-4 opacity-60 shrink-0" />
                {valid ? format(valid, 'MMM d, yyyy') : 'Pick a date'}
            </button>
            {open && (
                <div className="absolute z-[70] mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg p-2">
                    <div className="flex items-center gap-1 mb-2">
                        <button
                            type="button"
                            onClick={() => setMonth((m) => addMonths(m, -1))}
                            className="p-1 rounded hover:bg-gray-100"
                            aria-label="Previous month"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <select
                            value={month.getMonth()}
                            onChange={(e) => setMonth(new Date(month.getFullYear(), Number(e.target.value), 1))}
                            className="flex-1 text-xs border-gray-200 rounded-md py-1"
                            aria-label="Month"
                        >
                            {MONTHS.map((label, index) => (
                                <option key={label} value={index}>{label}</option>
                            ))}
                        </select>
                        <select
                            value={month.getFullYear()}
                            onChange={(e) => setMonth(new Date(Number(e.target.value), month.getMonth(), 1))}
                            className="text-xs border-gray-200 rounded-md py-1"
                            aria-label="Year"
                        >
                            {YEARS.map((year) => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => setMonth((m) => addMonths(m, 1))}
                            className="p-1 rounded hover:bg-gray-100"
                            aria-label="Next month"
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-7 text-center text-[11px] text-gray-400 mb-1">
                        {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                        {buildGrid(month).map((day) => {
                            const selected = valid && isSameDay(day, valid);
                            const inMonth = isSameMonth(day, month);
                            return (
                                <button
                                    key={day.toISOString()}
                                    type="button"
                                    onClick={() => pick(day)}
                                    className={`h-7 rounded text-xs tabular-nums transition-colors ${
                                        selected
                                            ? 'bg-primary text-white font-semibold'
                                            : inMonth
                                                ? 'text-gray-900 hover:bg-gray-100'
                                                : 'text-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {day.getDate()}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between px-1 pt-2 text-sm">
                        {allowClear ? (
                            <button
                                type="button"
                                className="text-primary hover:underline"
                                onClick={() => { onChange(''); setOpen(false); }}
                            >
                                Clear
                            </button>
                        ) : <span />}
                        <button
                            type="button"
                            className="text-primary hover:underline"
                            onClick={() => pick(new Date())}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
